import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import { CustomChip, Link } from '@hh.ru/magritte-ui';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';
import { makeSetStoreField } from '@hh.ru/redux-create-reducer/lib/createReducer';
import { usePush } from '@hh.ru/redux-spa-middleware';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { format } from 'bloko/common/trl';

import { fetchBannerClose } from 'src/components/Search/Resume/ContextVacancy/api/bannerClose';
import {
    AnalyticsParams,
    ResumeSearchContextAction,
    ResumeSearchContextSource,
    sendEventAction,
    sendEventElementShown,
} from 'src/components/Search/Resume/ContextVacancy/utils/analytics';
import { getUrlWithoutVacancy } from 'src/components/Search/Resume/ContextVacancy/utils/getUrlWithoutVacancy';
import TitleWrapper from 'src/components/Search/Resume/Header/Title/Wrapper';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { Filters } from 'src/models/resumeSearchContext';

import styles from './styles.less';

const setContextVacancySelectorVisible = makeSetStoreField('resumeSearchContextVacancySelectorVisible');

const TrlKeys = {
    text: 'resume.search.context.titleWithVacancy.text',
    actionChange: 'resume.search.context.titleWithVacancy.action.change',
};

interface Props {
    resumesCount: number;
    vacancyName: string;
    vacancyId: number;
    filters: Filters;
}

const ContextTitleWithVacancy: TranslatedComponent<Props> = ({
    resumesCount,
    vacancyName,
    vacancyId,
    filters,
    trls,
}) => {
    const dispatch = useDispatch();

    const push = usePush();
    const urlSearchParams = useSelectorNonNullable((state) => state.router.location.search);
    const handleDelete = useCallback(() => {
        void fetchBannerClose(true);
        push(getUrlWithoutVacancy(urlSearchParams));
    }, [push, urlSearchParams]);

    const employerId = useSelector((state) => state.employerId);
    const employerManagerId = useSelector((state) => state.employerManager?.id) || '';

    const analyticsParams: AnalyticsParams = useMemo(
        () => ({
            vacancyId,
            filters: JSON.stringify(filters.query),
            employerId,
            employerManagerId,
            hhtmSourceLabel: ResumeSearchContextSource.Title,
        }),
        [employerId, employerManagerId, filters.query, vacancyId]
    );

    return (
        <div data-qa="resume-search-context-title-with-vacancy">
            <ElementShownAnchor fn={sendEventElementShown} {...analyticsParams} className={styles.titleWithVacancy}>
                <TitleWrapper>{format(trls[TrlKeys.text], { '{0}': resumesCount })}</TitleWrapper>
                <div className={styles.vacancyChip}>
                    <CustomChip
                        size="small"
                        onDelete={() => {
                            handleDelete();
                            sendEventAction(ResumeSearchContextAction.Close, analyticsParams);
                        }}
                        deleteOnly
                        data-qa="resume-search-context-title-selected-vacancy"
                    >
                        {vacancyName}
                    </CustomChip>
                </div>
                <Link
                    inline
                    typography="label-2-regular"
                    onClick={() => {
                        dispatch(setContextVacancySelectorVisible(true));
                        sendEventAction(ResumeSearchContextAction.Change, analyticsParams);
                    }}
                    data-qa="resume-search-context-action-change"
                >
                    {trls[TrlKeys.actionChange]}
                </Link>
            </ElementShownAnchor>
        </div>
    );
};

export default translation(ContextTitleWithVacancy);
